import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AutocompleteFormField } from '@components/form-fields/AutocompleteFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ProposalPolicyPersonDrawer from '@features/production/proposal/drawers/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';

import {
  useGetBusinessPartnerPersons,
  useGetPersonAddressList
} from '../amendment.slice';
import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(2, 49%)`,
    gap: '2%',
    alignItems: 'center'
  }
}));

type Props = {
  disabledForm?: boolean;
  primaryBPId?: string;
  lineExternalCode?: string;
  personsOptions: Record<string, string>;
  setPersonsOptions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
};

const formatAddress = (it: any) => {
  const { building, street, city } = it?.address || {};
  const country = it?.country?.title;
  const zone = it?.zone?.title;

  return [building, street, zone, city, country].filter(Boolean).join(', ');
};

const mergeDedupByLabel = (
  base: Record<string, string>,
  incoming: Record<string, string>
): Record<string, string> => {
  const out: Record<string, string> = { ...base };
  const seen = new Set<string>(Object.values(base));
  for (const [id, label] of Object.entries(incoming || {})) {
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out[id] = label;
  }

  return out;
};

const extractCode = (label?: string) =>
  String(label ?? '')
    .split(' - ')[0]
    ?.trim() || '';

const OwnerWidgetAtom: React.FC<Props> = ({
  disabledForm,
  primaryBPId,
  lineExternalCode,
  personsOptions,
  setPersonsOptions
}) => {
  const { classes } = useStyles();
  const { id: amendmentId } = useParams<{ id: string }>();

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<FormValues>();

  const status = String(values?.status || '').toUpperCase();
  const isDisabled =
    status === 'CONFIRMED' || status === 'CANCELLED' || !!disabledForm;

  const payerFullName = values?.payerOwner?.payer?.fullName || '';
  const ownerFullName = values?.payerOwner?.owner?.fullName || '';
  const payerCode = extractCode(payerFullName);
  const ownerCode = extractCode(ownerFullName);

  const ownerValues = values?.payerOwner?.owner;
  const ownerErrors = (errors?.payerOwner as any)?.owner ?? {};
  const ownerTouched = (touched?.payerOwner as any)?.owner ?? {};

  const [personsQuery] = useGetBusinessPartnerPersons();
  const [addressesQuery] = useGetPersonAddressList();

  const mainbusinessPartnerID = useMemo(() => primaryBPId ?? '', [primaryBPId]);
  const ownerPath = (k: string) => `payerOwner.owner.${k}`;

  const [lists, setLists] = useState<{
    ownerAddresses: Record<string | number, string>;
  }>(() => {
    const addrId = ownerValues?.addressId;
    const addrText = ownerValues?.addressDetails;

    return {
      ownerAddresses: addrId && addrText ? { [addrId]: addrText } : {}
    };
  });

  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState(false);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState(false);

  useEffect(() => {
    const addrId = ownerValues?.addressId;
    const addrText = ownerValues?.addressDetails;

    if (addrId && addrText && !lists.ownerAddresses[addrId]) {
      setLists((prev) => ({
        ...prev,
        ownerAddresses: { ...prev.ownerAddresses, [addrId]: addrText }
      }));
    }
  }, [ownerValues?.addressId, ownerValues?.addressDetails]);

  const seedPersonsPool = useCallback(async () => {
    try {
      const res = await personsQuery({
        variables: {
          searchKeyword: '',
          mainbusinessPartnerID
        }
      });

      const items = res?.data?.salesforce?.getBusinessPartnerPersons || [];
      const pool: Record<string, string> = {};
      for (const i of items) {
        const label = `${i.code} - ${i.fullName}`;
        if (label) pool[i.id] = label;
      }

      if (Object.keys(pool).length) {
        setPersonsOptions((prev) => mergeDedupByLabel(prev, pool));
      }
    } catch {
      /* ignore */
    }
  }, [personsQuery, mainbusinessPartnerID, setPersonsOptions]);

  useEffect(() => {
    seedPersonsPool();
  }, []);

  const fetchAddresses = async (personId: string) => {
    const res = await addressesQuery({
      variables: { selectedPersonID: personId }
    });
    const items = res?.data?.salesforce?.getPersonAddressList?.items || [];
    const map: Record<string | number, string> = {};
    let primary: string | number = '';
    let primaryText = '';

    items.forEach((it: any) => {
      const addr = formatAddress(it);
      map[it.id] = addr;
      if (it.isPrimary && !primary) {
        primary = it.id;
        primaryText = addr;
      }
    });
    if (!primary && items[0]) {
      primary = items[0].id;
      primaryText = formatAddress(items[0]);
    }

    return { map, primary, primaryText };
  };

  const onOwnerChange = async (v: any) => {
    await setFieldValue(ownerPath('id'), v, false);

    const label =
      personsOptions[v] ||
      (ownerValues?.fullName && v === ownerValues?.id
        ? ownerValues.fullName
        : '') ||
      '';

    if (label) {
      await setFieldValue(ownerPath('fullName'), label, false);
    }

    if (!isEmpty(v)) {
      const { map, primary, primaryText } = await fetchAddresses(String(v));
      setLists((prev) => ({
        ...prev,
        ownerAddresses: { ...map }
      }));
      await setFieldValue(ownerPath('addressId'), primary || '', false);
      await setFieldValue(
        ownerPath('addressDetails'),
        primaryText || '',
        false
      );
    } else {
      setLists((prev) => ({ ...prev, ownerAddresses: {} }));
      await setFieldValue(ownerPath('addressId'), '', false);
      await setFieldValue(ownerPath('addressDetails'), '', false);
    }
  };

  const ownerIsDifferentValue = values?.payerOwner?.ownerIsDifferent;

  if (!ownerIsDifferentValue) {
    return null;
  }

  if (payerCode && ownerCode && payerCode === ownerCode) {
    return null;
  }

  return (
    <WidgetSection title="Owner">
      <div className={classes.fieldRow}>
        {newPersonDrawerOpen && (
          <ProposalPolicyPersonDrawer
            open
            onClose={() => setNewPersonDrawerOpen(false)}
            section="owner"
            mainBusinessPartnerId={primaryBPId ?? ''}
            lineExternalCode={lineExternalCode ?? ''}
            onSubmitSuccess={async (person) => {
              const personId = person.id;
              const fullName = `${person.code} - ${person.fullName}`;
              setPersonsOptions((prev) =>
                mergeDedupByLabel(prev, { [personId]: fullName })
              );

              const { map, primary, primaryText } = await fetchAddresses(
                String(personId)
              );
              setLists((prev) => ({
                ...prev,
                ownerAddresses: { ...prev.ownerAddresses, ...map }
              }));
              await setFieldValue(ownerPath('id'), personId, false);
              await setFieldValue(ownerPath('fullName'), fullName, false);
              await setFieldValue(ownerPath('addressId'), primary || '', false);
              await setFieldValue(
                ownerPath('addressDetails'),
                primaryText || '',
                false
              );
              setNewPersonDrawerOpen(false);
            }}
          />
        )}

        {newAddressDrawerOpen && (
          <PersonAddressDrawer
            open
            isProposal
            onClose={() => setNewAddressDrawerOpen(false)}
            personId={ownerValues?.id}
            onSubmitSuccess={async (addrId, addrText) => {
              const personId = ownerValues?.id;
              setLists((prev) => ({
                ...prev,
                ownerAddresses: { ...prev.ownerAddresses, [addrId]: addrText }
              }));

              await setFieldValue(ownerPath('addressId'), addrId, false);
              await setFieldValue(ownerPath('addressDetails'), addrText, false);
              const insureds = (values as any)?.insureds || [];
              for (let i = 0; i < insureds.length; i++) {
                if (insureds[i].id === personId) {
                  await setFieldValue(
                    `insureds[${i}].addressId`,
                    addrId,
                    false
                  );
                  await setFieldValue(
                    `insureds[${i}].addressDetails`,
                    addrText,
                    false
                  );
                }
              }

              const beneficiaries = (values as any)?.beneficiaries || [];
              for (let i = 0; i < beneficiaries.length; i++) {
                if (beneficiaries[i].id === personId) {
                  await setFieldValue(
                    `beneficiaries[${i}].addressId`,
                    addrId,
                    false
                  );
                  await setFieldValue(
                    `beneficiaries[${i}].addressDetails`,
                    addrText,
                    false
                  );
                }
              }

              if ((values as any)?.payerOwner?.payer?.id === personId) {
                await setFieldValue(
                  'payerOwner.payer.addressId',
                  addrId,
                  false
                );
                await setFieldValue(
                  'payerOwner.payer.addressDetails',
                  addrText,
                  false
                );
              }

              setNewAddressDrawerOpen(false);
            }}
            onSuccess={() => undefined}
          />
        )}

        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Person'}
          addSpace={!isDisabled}
          onLinkClick={(e?: any) => {
            e?.preventDefault?.();
            setNewPersonDrawerOpen(true);
          }}
        >
          <AutocompleteFormField
            name="payerOwner.owner.id"
            title="Owner*"
            placeholder="Type to search for Person"
            value={ownerValues?.id}
            error={ownerTouched?.id ? ownerErrors?.id : ''}
            material
            disabled={isDisabled}
            onChange={onOwnerChange}
            onBlur={(_, e) => handleBlur(e)}
            initialOptions={personsOptions}
            fetchOptions={async (
              searchKeyword
            ): Promise<Record<string, string>> => {
              const q = (searchKeyword ?? '').trim();
              const res = await personsQuery({
                variables: {
                  searchKeyword: q,
                  mainbusinessPartnerID
                }
              });

              const items =
                res?.data?.salesforce?.getBusinessPartnerPersons || [];
              const raw = items.map((i: any) => ({
                id: i.id,
                code: i.code,
                fullName: i.fullName,
                label: `${i.code} - ${i.fullName}`
              }));

              const seen = new Set<string>(Object.values(personsOptions || {}));
              const deduped: Record<string, string> = {};
              for (const r of raw) {
                if (!r.label || seen.has(r.label)) continue;
                seen.add(r.label);
                deduped[r.id] = r.label;
              }

              setPersonsOptions((prev) => mergeDedupByLabel(prev, deduped));

              return deduped;
            }}
          />
        </WithLinkFormField>

        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Address'}
          addSpace={!isDisabled}
          onLinkClick={(e?: any) => {
            e?.preventDefault?.();
            setNewAddressDrawerOpen(true);
          }}
        >
          <SelectFormField
            name="payerOwner.owner.addressId"
            title="Owner Address*"
            placeholder="Owner Address"
            value={ownerValues?.addressId}
            error={ownerTouched?.addressId ? ownerErrors?.addressId : ''}
            material
            disabled
            onBlur={(_, e) => handleBlur(e)}
            selectOptions={lists.ownerAddresses}
          />
        </WithLinkFormField>
      </div>
    </WidgetSection>
  );
};

export { OwnerWidgetAtom };
